import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Slider } from "@mantine/core";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { EnrichmentScenario } from "../api/types";
import { Spinner } from "./Spinner";

/* Who a campaign wants to reach, and the options api.set_enrichment_options
 * saves with it.
 *
 * The fields are the builder's Contacts: a scenario from the ICP framework
 * fills in the roles in priority order and the seniorities, one line says
 * what is set, Change opens the roles, the seniorities and a contact state
 * to edit, and the slider sets the campaign's minimum fit. The engine scores
 * every person connected to a property from 0 to 100 for how well they fit
 * the campaign (engine docs/campaign-people.md), and the slider is the
 * lowest score that makes the list, nothing more. Beside it, two limits say
 * how many people to take per role at each company and per company at each
 * property, empty for no limit, which is the default (James, 2026-10-14).
 * The counts come from the campaign's own people once its list has been
 * made, under the limits on the form (api.campaign_cut_preview, engine
 * migrations 233 and 242). A stop in the not-recommended range is red.
 * Automated enrichment and the request for
 * manual enrichment are asked under the builder's Enrichment, and on a saved
 * campaign's Enrichment tab, so they are not fields here.
 *
 * Every list here is the engine's (api.enrichment_scenario,
 * api.role_category, api.seniority_level, api.campaign_cut_preview), so a
 * category, a level, a stop's words or a count changes without a deploy. */
export type EnrichmentOptions = {
  autoEnrich: boolean;
  askEnricher: boolean;
  roles: string[];
  seniorities: string[];
  contactState: string;
  researchNotes: string;
  /** The campaign's minimum fit, 10 to 100 in steps of 10. Null means the default. */
  contactCut: number | null;
  /** The most people in one role at one company at a property. Null is no limit. */
  perRole: number | null;
  /** The most people from one company at a property. Null is no limit. */
  perCompany: number | null;
  /** With a per-role limit, also take the best reachable person in a role that has none. */
  reachFill: boolean;
};

export const NEW_CAMPAIGN_OPTIONS: EnrichmentOptions = {
  autoEnrich: true, askEnricher: true, roles: [], seniorities: [], contactState: "", researchNotes: "", contactCut: null,
  perRole: null, perCompany: null, reachFill: true,
};

/* The options a saved campaign has, from api.campaign. askEnricher is
   not stored: a request is its own state, shown on the page. */
export function optionsFrom(c: {
  auto_enrich?: boolean | null; roles?: string[] | null; seniorities?: string[] | null;
  contact_state?: string | null; research?: unknown; contact_cut?: number | null;
  contact_per_role?: number | null; contact_per_company?: number | null; contact_reach_fill?: boolean | null;
}): EnrichmentOptions {
  const research = (c.research ?? {}) as { notes?: unknown };
  return {
    autoEnrich: Boolean(c.auto_enrich),
    askEnricher: false,
    roles: c.roles ?? [],
    seniorities: c.seniorities ?? [],
    contactState: c.contact_state ?? "",
    researchNotes: typeof research.notes === "string" ? research.notes : "",
    contactCut: c.contact_cut ?? null,
    perRole: c.contact_per_role ?? null,
    perCompany: c.contact_per_company ?? null,
    reachFill: c.contact_reach_fill ?? true,
  };
}

/** The limits in words, for the Criteria and Enrichment tabs. */
export function limitsWords(perRole: number | null | undefined, perCompany: number | null | undefined): string {
  if (!perRole && !perCompany) return "No limit per role type or company.";
  const parts = [];
  if (perRole) parts.push(`up to ${perRole} per role type at each company`);
  if (perCompany) parts.push(`up to ${perCompany} per company at each property`);
  const text = parts.join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1) + ".";
}

type RoleRow = { name: string | null; sort_order: number | null };
type LevelRow = { level: string | null; word: string | null; sort_order: number | null };

export function useEnrichmentChoices(token: string) {
  return useQuery({
    queryKey: ["enrichment-choices"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const api = apiClient(token);
      const [roles, levels, scenarios] = await Promise.all([
        api.from("role_category").select("name,sort_order").order("sort_order").limit(50),
        api.from("seniority_level").select("level,word,sort_order").order("sort_order").limit(20),
        api.from("enrichment_scenario").select("key,name,roles,seniorities,sort_order").order("sort_order").limit(20),
      ]);
      for (const r of [roles, levels, scenarios]) if (r.error) throw new Error(apiErrorMessage(r.error));
      return {
        roles: (roles.data ?? []) as RoleRow[],
        levels: (levels.data ?? []) as LevelRow[],
        scenarios: (scenarios.data ?? []) as EnrichmentScenario[],
      };
    },
  });
}

/** One stop of the minimum fit slider, from api.campaign_cut_preview. */
export type CutStop = {
  cut: number;
  people: number;
  properties_with_somebody: number;
  properties_with_nobody: number;
  people_without_mobile: number;
  in_red: boolean;
  is_current: boolean;
  words: string | null;
  /** How the fit score adds up, in one sentence from the engine's points. */
  score_words: string | null;
  /** The engine's default minimum fit (engine migration 308). */
  is_default: boolean;
};

/** The limits a preview counts under: the form's, so a change shows before it is saved. */
export type Limits = { perRole: number | null; perCompany: number | null; reachFill: boolean };

/** The ten stops of the minimum fit slider for a campaign: the counts from
 *  its own people under the limits given, the words beside each stop,
 *  whether a stop is red, and which is the minimum in use. For a campaign
 *  with no people taken yet the counts are zero and the minimum in use is
 *  the default. */
export function useCutPreview(token: string, campaign: string | null | undefined, limits?: Limits) {
  return useQuery({
    queryKey: ["cut-preview", campaign ?? "", limits?.perRole ?? null, limits?.perCompany ?? null, limits?.reachFill ?? true],
    queryFn: () => callFunction<CutStop[]>(token, "campaign_cut_preview", {
      p_name: campaign || "new",
      ...(limits?.perRole ? { p_per_role: limits.perRole } : {}),
      ...(limits?.perCompany ? { p_per_company: limits.perCompany } : {}),
      p_reach_fill: limits?.reachFill ?? true,
    }),
  });
}

/** The slider: the minimum fit, the words beside it, and the counts at that stop. */
export function CutSlider({ token, campaign, value, onChange, limits }: {
  token: string;
  /** The campaign whose counts show, or null for one not made yet. */
  campaign: string | null;
  /** The campaign's own minimum fit, or null for the engine's default. */
  value: number | null;
  /** Null puts the campaign back on the default. */
  onChange: (cut: number | null) => void;
  limits?: Limits;
}) {
  const preview = useCutPreview(token, campaign, limits);
  const stops = preview.data ?? [];
  const current = stops.find((s) => s.is_current);
  // Null is the default, and which stop that is is the engine's to say. The
  // stop in use is the fallback for an engine before migration 308.
  const byDefault = stops.find((s) => s.is_default);
  const cut = value ?? byDefault?.cut ?? current?.cut ?? 50;
  const at = stops.find((s) => s.cut === cut);
  const hasCounts = stops.some((s) => s.people > 0);
  return (
    <div className="cut">
      <div className="cut-line">
        <span className="field-label">Minimum fit</span>
        <span className="cut-now">
          <strong className={at?.in_red ? "refusal" : undefined} aria-live="polite">{cut}</strong>
          {value !== null ? (
            <button type="button" className="link" onClick={() => onChange(null)}>
              Use the default{byDefault ? ` (${byDefault.cut})` : ""}
            </button>
          ) : null}
        </span>
      </div>
      <Slider value={cut} onChange={onChange} min={10} max={100} step={10} label={null}
              marks={stops.length ? stops.map((s) => ({ value: s.cut })) : [10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => ({ value: v }))}
              {...(at?.in_red ? { color: "red" } : {})} aria-label="Minimum fit" />
      <p className={at?.in_red ? "hint refusal" : "hint"}>
        {preview.isError ? (preview.error as Error).message : (at?.words ?? "")}
        {value === null && (byDefault ?? current) && !preview.isError ? " The default." : ""}
      </p>
      {stops[0]?.score_words ? <p className="muted cut-score">{stops[0].score_words}</p> : null}
      <p className="muted cut-counts">
        {preview.isLoading ? <Spinner />
          : !hasCounts ? "The counts show once the list is made."
          : at ? `${at.people.toLocaleString()} people, ${at.properties_with_nobody.toLocaleString()} ${at.properties_with_nobody === 1 ? "property" : "properties"} without a person, ${at.people_without_mobile.toLocaleString()} without a mobile.`
          : null}
      </p>
    </div>
  );
}

export function EnrichmentOptionsFields({ token, value, onChange, campaign }: {
  token: string;
  value: EnrichmentOptions;
  onChange: (next: EnrichmentOptions) => void;
  /** The campaign the slider's counts come from. Null for one not made yet. */
  campaign?: string | null;
}) {
  const choices = useEnrichmentChoices(token);
  const [changing, setChanging] = useState(false);
  if (choices.isError) return <p className="refusal">{(choices.error as Error).message}</p>;
  if (choices.isLoading || !choices.data) return <p className="muted"><Spinner />Loading.</p>;
  const { roles, levels, scenarios } = choices.data;

  const scenarioOn = (s: EnrichmentScenario) =>
    JSON.stringify(s.roles ?? []) === JSON.stringify(value.roles)
    && JSON.stringify([...(s.seniorities ?? [])].sort()) === JSON.stringify([...value.seniorities].sort());

  const toggleRole = (name: string) => {
    // The order of choosing is the priority. Choosing adds to the end, and
    // the list below says the order in words.
    const next = value.roles.includes(name) ? value.roles.filter((r) => r !== name) : [...value.roles, name];
    onChange({ ...value, roles: next });
  };
  const toggleLevel = (level: string) => {
    const next = value.seniorities.includes(level)
      ? value.seniorities.filter((l) => l !== level) : [...value.seniorities, level];
    onChange({ ...value, seniorities: next });
  };

  return (
    <div className="enrichment-options">
      <div className="bar">
        {scenarios.map((s) => (
          <button type="button" key={s.key ?? s.name ?? ""} aria-pressed={scenarioOn(s)}
                  className={scenarioOn(s) ? "pressed" : undefined}
                  onClick={() => onChange({ ...value, roles: s.roles ?? [], seniorities: s.seniorities ?? [] })}>
            {s.name}
          </button>
        ))}
      </div>
      <p className="hint">
        {value.roles.length
          ? <>In this order: {value.roles.join(", ")}.</>
          : <>Each property's own order: new construction, existing or property management.</>}
        {value.seniorities.length
          ? <> {levels.filter((l) => l.level && value.seniorities.includes(l.level)).map((l) => l.word).join(", ")}.</> : null}
        {value.contactState ? <> In {value.contactState}.</> : null}
        {" "}<button type="button" className="link" aria-expanded={changing} onClick={() => setChanging((v) => !v)}>
          {changing ? "Done" : "Change"}
        </button>
      </p>
      {!changing ? null : <>
      <div className="fields">
        <label>
          <div className="field-label">Role types, in the order they are wanted</div>
          <div className="checks">
            {roles.map((r) => r.name ? (
              <label key={r.name} className="check">
                <input type="checkbox" checked={value.roles.includes(r.name)} onChange={() => toggleRole(r.name as string)} />
                {" "}{r.name}
                {value.roles.includes(r.name) ? <span className="muted"> {value.roles.indexOf(r.name) + 1}</span> : null}
              </label>
            ) : null)}
          </div>
        </label>
        <label>
          <div className="field-label">Seniority</div>
          <div className="checks">
            {levels.map((l) => l.level ? (
              <label key={l.level} className="check">
                <input type="checkbox" checked={value.seniorities.includes(l.level)} onChange={() => toggleLevel(l.level as string)} />
                {" "}{l.word}
              </label>
            ) : null)}
          </div>
        </label>
        <label>
          <div className="field-label">People located in</div>
          <input value={value.contactState} maxLength={2} style={{ width: "4rem" }}
                 onChange={(e) => onChange({ ...value, contactState: e.target.value.toUpperCase() })} />
          <div className="muted" style={{ fontSize: 12 }}>A two-letter state, or empty for anywhere.</div>
        </label>
      </div>
      </>}
      <CutSlider token={token} campaign={campaign ?? null} value={value.contactCut}
                 onChange={(cut) => onChange({ ...value, contactCut: cut })}
                 limits={{ perRole: value.perRole, perCompany: value.perCompany, reachFill: value.reachFill }} />
      <LimitFields value={value} onChange={onChange} />
    </div>
  );
}

/** A count from a number field: a whole number of 1 or more, or null for no limit. */
function limitFrom(text: string): number | null {
  const n = Number.parseInt(text, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/** The two limits and the reach setting. Empty is no limit, the default. */
function LimitFields({ value, onChange }: { value: EnrichmentOptions; onChange: (next: EnrichmentOptions) => void }) {
  return (
    <div className="fields cut-limits">
      <label>
        <div className="field-label">People per role type at each company</div>
        <input type="number" min={1} step={1} inputMode="numeric" style={{ width: "7rem" }}
               value={value.perRole ?? ""}
               onChange={(e) => onChange({ ...value, perRole: limitFrom(e.target.value) })} />
        <div className="muted" style={{ fontSize: 12 }}>Empty for no limit.</div>
      </label>
      <label>
        <div className="field-label">People per company at each property</div>
        <input type="number" min={1} step={1} inputMode="numeric" style={{ width: "7rem" }}
               value={value.perCompany ?? ""}
               onChange={(e) => onChange({ ...value, perCompany: limitFrom(e.target.value) })} />
        <div className="muted" style={{ fontSize: 12 }}>Empty for no limit.</div>
      </label>
      {value.perRole ? (
        <label className="check">
          <input type="checkbox" checked={value.reachFill}
                 onChange={(e) => onChange({ ...value, reachFill: e.target.checked })} />
          {" "}Also add the best reachable person when a role type has none
        </label>
      ) : null}
    </div>
  );
}

/** The arguments api.set_enrichment_options takes, from the fields. */
export function optionsArgs(name: string, v: EnrichmentOptions) {
  return {
    p_name: name,
    p_auto_enrich: v.autoEnrich,
    p_roles: v.roles,
    p_seniorities: v.seniorities,
    // An empty string clears the campaign's state (engine migration 305), so
    // a state taken off the form is taken off the campaign.
    p_contact_state: v.contactState.trim(),
    p_research: { notes: v.researchNotes.trim() },
    // 0 puts the minimum fit back on the default (engine migration 305), so
    // Use the default on the form is the default on the campaign.
    p_contact_cut: v.contactCut ?? 0,
    // 0 clears a limit, so a limit taken off the form is taken off the campaign.
    p_per_role: v.perRole ?? 0,
    p_per_company: v.perCompany ?? 0,
    p_reach_fill: v.reachFill,
  };
}
